import { useEffect } from 'react'

import { useLazyQuery } from '@apollo/client'
import { Badge, Loader, Modal, Text } from '@mantine/core'
import dayjs from 'dayjs'

import { GET_PERMISSION_AUDIT_LOG } from 'src/graphql/permissions.graphql'

interface Permission {
  key: string
  description: string
}

interface PermissionHistoryModalProps {
  opened: boolean
  onClose: () => void
  employeeId: string
  allPermissions: Permission[]
}

const PermissionHistoryModal = ({
  opened,
  onClose,
  employeeId,
  allPermissions,
}: PermissionHistoryModalProps) => {
  const [fetchAuditLog, { data, loading }] = useLazyQuery(
    GET_PERMISSION_AUDIT_LOG,
    { fetchPolicy: 'network-only' }
  )

  useEffect(() => {
    if (opened && employeeId) {
      fetchAuditLog({ variables: { employeeId } })
    }
  }, [opened, employeeId, fetchAuditLog])

  const entries = data?.permissionAuditLog ?? []

  const getPermissionDescription = (permissionKey: string) => {
    const perm = allPermissions.find((p) => p.key === permissionKey)
    return perm?.description ?? permissionKey
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      title="Permission History"
      classNames={{ title: 'font-medium' }}
      size="md"
    >
      {loading ? (
        <div className="flex justify-center py-6">
          <Loader size="sm" color="gray" />
        </div>
      ) : entries.length === 0 ? (
        <Text size="sm" c="gray.6" className="py-6 text-center">
          No permission changes recorded yet.
        </Text>
      ) : (
        <div className="flex flex-col gap-3">
          {entries.map((entry) => (
            <div
              key={entry.auditLogId}
              className="flex items-start justify-between rounded-md border border-doubleNickel-gray-200 bg-doubleNickel-gray-50 px-3 py-2"
            >
              <div className="flex flex-col gap-1">
                <Text size="xs" fw={500}>
                  {getPermissionDescription(entry.permissionKey)}
                </Text>
                <Text size="xs" c="gray.6">
                  by {entry.actorName} ·{' '}
                  {dayjs(entry.createdAt).format('MMM D, YYYY [at] h:mm A')}
                </Text>
              </div>
              <Badge
                size="xs"
                color={entry.action === 'GRANTED' ? 'green' : 'red'}
                variant="light"
                className="mt-0.5 shrink-0"
              >
                {entry.action}
              </Badge>
            </div>
          ))}
        </div>
      )}
    </Modal>
  )
}

export default PermissionHistoryModal
